/**
 * Page principale Emplois du Temps Examens.
 *
 * Onglets :
 * 1. Configuration — Créer/configurer une session d'examens
 * 2. Visualisation — Voir les emplois du temps générés
 * 3. Impression — Imprimer en A3 ou A4
 * 4. Récap Salles — Salles occupées et effectifs par jour
 */

import React, { useState, useEffect } from 'react';
import { Calendar, Settings, Eye, Printer, Plus, Trash2, Play, Loader2, AlertTriangle, CheckCircle, ShieldCheck, Clock, X, Layers, Shuffle, Building2, Tag } from 'lucide-react';
import { useSelector } from 'react-redux';
import { RootState } from '../../store';
import {
  useGetSessionsQuery,
  useCreateSessionMutation,
  useUpdateSessionMutation,
  useDeleteSessionMutation,
  useToggleVisibiliteSessionMutation,
  useLazyPreValidationQuery,
  useGenererEmploiTempsMutation,
  useGetNiveauxDisponiblesQuery,
  useVerifierCompletudeQuery,
  useGetAlertesEnseignantsQuery,
  useSynchroniserCommunesMutation,
  useAjouterJourSessionMutation,
  useAjouterHoraireSessionMutation,
  SessionExamen,
  GenerationResultat,
  PreValidationResult,
  AlerteEnseignant,
} from '../../services/emploisTempsApi';
import {
  useGenererBrassagePourSessionMutation,
  GenererBrassageSessionResponse,
} from '../../services/brassageApi';
import { getAnneeAcademique } from '../../services/baseApi';
import EmploiTempsTableau from './EmploiTempsTableau';
import EmploiTempsImpression from './EmploiTempsImpression';
import EtiquettesImpression from './EtiquettesImpression';
import PauseControl from './PauseControl';
import RecapSalles from './RecapSalles';
import EstimationSujets from './EstimationSujets';
import RapportGeneration from './RapportGeneration';
import PreValidationModal from './PreValidationModal';
import DiagnosticMatieresModal from './DiagnosticMatieresModal';
import ConceptionManuelle from './ConceptionManuelle';

type TabType = 'config' | 'visualisation' | 'impression' | 'recap' | 'sujets' | 'etiquettes';

// ============================================================================
// Composant Alertes Enseignants Multiples
// ============================================================================

const AlerteEnseignantCard: React.FC<{ alerte: AlerteEnseignant; isDark: boolean }> = ({ alerte, isDark }) => {
  const severityBg = alerte.severite === 'warning'
    ? (isDark ? 'bg-orange-900/20 border-orange-800' : 'bg-white border-orange-200')
    : (isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200');

  return (
    <div className={`flex items-start gap-3 p-3 rounded-lg border ${severityBg}`}>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className={`font-medium text-sm ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>
            {alerte.nom}
          </span>
          <span className={`text-xs px-1.5 py-0.5 rounded font-mono ${isDark ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-600'}`}>
            {alerte.code}
          </span>
          {alerte.niveau && (
            <span className={`text-xs px-1.5 py-0.5 rounded font-medium ${
              alerte.niveau === 'L1' ? 'bg-blue-100 text-blue-700' :
              alerte.niveau === 'L2' ? 'bg-green-100 text-green-700' :
              'bg-purple-100 text-purple-700'
            }`}>{alerte.niveau}</span>
          )}
          {alerte.option && (
            <span className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{alerte.option}</span>
          )}
          <span className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>S{alerte.semestre}</span>
        </div>
        <div className="mt-1.5 flex flex-wrap gap-1.5">
          {alerte.enseignants.map((ens, idx) => (
            <span
              key={ens.enseignant_id}
              className={`inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full border ${
                idx === 0
                  ? (isDark ? 'bg-green-900/30 border-green-700 text-green-300' : 'bg-green-50 border-green-300 text-green-700')
                  : (isDark ? 'bg-red-900/20 border-red-700 text-red-300' : 'bg-red-50 border-red-300 text-red-700')
              }`}
            >
              <span className="font-medium">{ens.nom}</span>
              {ens.specialite && <span className="opacity-60">({ens.specialite})</span>}
              {idx === 0 && <span className="text-[10px] opacity-70">← utilisé</span>}
            </span>
          ))}
        </div>
      </div>
      <span className={`shrink-0 text-xs font-bold px-2 py-1 rounded ${
        alerte.severite === 'warning'
          ? 'bg-orange-100 text-orange-700'
          : 'bg-blue-100 text-blue-600'
      }`}>
        {alerte.nb_enseignants} ens.
      </span>
    </div>
  );
};

const AlertesEnseignantsPanel: React.FC<{ sessionId: number; isDark: boolean }> = ({ sessionId, isDark }) => {
  const { data, isLoading } = useGetAlertesEnseignantsQuery(sessionId);
  const [expanded, setExpanded] = React.useState(false);

  if (isLoading || !data || data.total_alertes === 0) return null;

  const alertesDansEmploi = data.alertes.filter((a: AlerteEnseignant) => a.dans_emploi_du_temps);
  const alertesHors = data.alertes.filter((a: AlerteEnseignant) => !a.dans_emploi_du_temps);
  const hasWarnings = alertesDansEmploi.length > 0;

  return (
    <div className={`rounded-xl border overflow-hidden ${
      hasWarnings
        ? (isDark ? 'bg-orange-900/15 border-orange-700' : 'bg-orange-50 border-orange-200')
        : (isDark ? 'bg-blue-900/15 border-blue-700' : 'bg-blue-50 border-blue-200')
    }`}>
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between px-4 py-3 text-left"
      >
        <div className="flex items-center gap-3">
          <AlertTriangle size={18} className={hasWarnings ? 'text-orange-500' : 'text-blue-500'} />
          <div>
            <span className={`font-semibold text-sm ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>
              {data.total_alertes} matière(s) avec plusieurs enseignants
            </span>
            {hasWarnings && (
              <span className="text-xs text-orange-600 ml-2">
                dont {alertesDansEmploi.length} dans l'emploi du temps
              </span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
            {expanded ? 'Masquer' : 'Voir le détail'}
          </span>
          {expanded ? <span>▲</span> : <span>▼</span>}
        </div>
      </button>

      {expanded && (
        <div className="px-4 pb-4 space-y-3">
          <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
            Ces matières ont plusieurs enseignants attribués. Lors de la génération, seul le premier est utilisé.
            Vérifiez et corrigez si nécessaire.
          </p>

          {alertesDansEmploi.length > 0 && (
            <div>
              <div className={`text-xs font-bold mb-2 ${isDark ? 'text-orange-400' : 'text-orange-700'}`}>
                Dans l'emploi du temps ({alertesDansEmploi.length})
              </div>
              <div className="space-y-2">
                {alertesDansEmploi.map((a: AlerteEnseignant) => (
                  <AlerteEnseignantCard key={a.matiere_id} alerte={a} isDark={isDark} />
                ))}
              </div>
            </div>
          )}

          {alertesHors.length > 0 && (
            <div>
              <div className={`text-xs font-bold mb-2 ${isDark ? 'text-blue-400' : 'text-blue-600'}`}>
                Hors emploi du temps ({alertesHors.length})
              </div>
              <div className="space-y-2">
                {alertesHors.map((a: AlerteEnseignant) => (
                  <AlerteEnseignantCard key={a.matiere_id} alerte={a} isDark={isDark} />
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ============================================================================

const EmploisTempsPage: React.FC = () => {
  const isDark = useSelector((state: RootState) => state.ui?.theme === 'dark');
  const anneeAcademique = getAnneeAcademique() || '2024-2025';

  const [activeTab, setActiveTab] = useState<TabType>('config');
  const [selectedSession, setSelectedSession] = useState<SessionExamen | null>(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [createError, setCreateError] = useState<string>('');
  const [generationResult, setGenerationResult] = useState<GenerationResultat | null>(null);
  const [selectedNiveau, setSelectedNiveau] = useState('L1');
  const [selectedPole, setSelectedPole] = useState('polytechnique');
  // Multi-sélection visualisation : quand non vide, on ignore selectedNiveau/selectedPole
  // et on rend un tableau par combinaison présente dans niveauxDispo.
  const [visuNiveaux, setVisuNiveaux] = useState<string[]>([]);
  const [visuPoles, setVisuPoles] = useState<string[]>([]);

  // Form state
  const [formData, setFormData] = useState({
    nom: `Examen Partiel Session Ordinaire`,
    semestre: 1,
    type_session: 'ordinaire',
    date_debut: '',
    date_fin: '',
    type_formation: 'FI',
    mode_conception: 'automatique',
  });

  // API
  const { data: sessions = [], isLoading: loadingSessions, refetch: refetchSessions } = useGetSessionsQuery({
    annee_academique: anneeAcademique,
  });
  const [createSession, { isLoading: creating }] = useCreateSessionMutation();
  const [deleteSession] = useDeleteSessionMutation();
  const [updateSession] = useUpdateSessionMutation();
  const [genererEmploiTemps, { isLoading: generating }] = useGenererEmploiTempsMutation();
  const [synchroniserCommunes, { isLoading: syncingCommunes }] = useSynchroniserCommunesMutation();
  const [ajouterJourSession, { isLoading: ajoutingJour }] = useAjouterJourSessionMutation();
  const [ajouterHoraireSession, { isLoading: ajoutingHoraire }] = useAjouterHoraireSessionMutation();
  const [toggleVisibilite] = useToggleVisibiliteSessionMutation();

  const handleToggleVisibilite = async (session: SessionExamen, e: React.MouseEvent) => {
    e.stopPropagation();
    const willBeVisible = !session.visible_aux_etudiants;
    if (!window.confirm(
      willBeVisible
        ? `Rendre l'emploi du temps « ${session.nom} » visible aux étudiants dans leur portail ?`
        : `Masquer l'emploi du temps « ${session.nom} » des étudiants ?`
    )) return;
    try {
      await toggleVisibilite({ sessionId: session.id, visible: willBeVisible }).unwrap();
    } catch (err: any) {
      alert(err?.data?.detail || 'Erreur');
    }
  };
  const [triggerPreValidation, { isLoading: validating }] = useLazyPreValidationQuery();
  const [genererBrassage, { isLoading: generatingBrassage }] = useGenererBrassagePourSessionMutation();
  const [brassageResult, setBrassageResult] = useState<GenererBrassageSessionResponse | null>(null);
  const [showPreValidation, setShowPreValidation] = useState(false);
  const [preValidationResult, setPreValidationResult] = useState<PreValidationResult | null>(null);
  const [showVerification, setShowVerification] = useState(false);
  const [showDureeConfig, setShowDureeConfig] = useState(false);
  const [showPlanifConfig, setShowPlanifConfig] = useState(false);
  const [showDiagnosticMatieres, setShowDiagnosticMatieres] = useState(false);
  const [dureeMatiere, setDureeMatiere] = useState('');
  const [dureeValeur, setDureeValeur] = useState('02:30');

  const { data: niveauxDispo = [] } = useGetNiveauxDisponiblesQuery(
    selectedSession?.id || 0,
    { skip: !selectedSession }
  );

  const { data: verification, refetch: refetchVerification } = useVerifierCompletudeQuery(
    selectedSession?.id || 0,
    { skip: !selectedSession || !showVerification }
  );

  // Auto-sélectionner la première session
  useEffect(() => {
    if (sessions.length > 0 && !selectedSession) {
      setSelectedSession(sessions[0]);
    }
  }, [sessions, selectedSession]);

  const handleCreateSession = async () => {
    setCreateError('');
    try {
      const result = await createSession({
        ...formData,
        type_formation: formData.type_formation as 'FI' | 'CS',
        mode_conception: formData.mode_conception as 'automatique' | 'manuel',
        annee_academique: anneeAcademique,
        contraintes: {
          type_formation: formData.type_formation as 'FI' | 'CS',
          creneaux_horaires: [
            { heure_debut: '08:00', heure_fin: '10:00', label: '08H-10H' },
            { heure_debut: '11:00', heure_fin: '13:00', label: '11H-13H' },
            { heure_debut: '14:00', heure_fin: '16:00', label: '14H-16H' },
          ],
          max_matieres_par_jour: 3,
          max_matieres_samedi: 3,
          max_matieres_l3: 3,
          jour1_une_seule_matiere: false,
          jour1_heure_debut: '08:00',
          l3_heure_debut: '08:00',
          composer_dimanche: false,
          composer_samedi: true,
          alternance_poles: true,
          alternance_l2_l3: false,
          skip_jour_apres_premier: false,
          separer_fi_cs: true,
          affectation_sites: {
            'L1_polytechnique': [4],
            'L2_polytechnique': [4],
            'L3_polytechnique': [4],
            'L1_commerce_juridique': [1, 2, 3],
            'L2_commerce_juridique': [1, 2, 3],
            'L3_commerce_juridique': [1, 2, 3],
          },
        },
      }).unwrap();
      setSelectedSession(result);
      setShowCreateForm(false);
      refetchSessions();
    } catch (err: any) {
      console.error('Erreur création session:', err);
      // Extraire le message d'erreur (DRF renvoie {error}, {detail}, ou un dict de champs)
      const data = err?.data;
      let msg = '';
      if (typeof data === 'string') msg = data;
      else if (data?.error) msg = data.error;
      else if (data?.detail) msg = data.detail;
      else if (data?.non_field_errors) {
        msg = Array.isArray(data.non_field_errors)
          ? data.non_field_errors.join(' ')
          : String(data.non_field_errors);
      } else if (data && typeof data === 'object') {
        msg = Object.entries(data)
          .map(([k, v]) => `${k} : ${Array.isArray(v) ? v.join(', ') : v}`)
          .join(' • ');
      }
      if (!msg && err?.status === 500) {
        msg = "Une session existe déjà pour cette année, ce semestre et ce type de session. "
          + "Sélectionnez-la dans la liste pour la modifier, ou choisissez d'autres critères.";
      }
      setCreateError(msg || "Erreur lors de la création de la session.");
    }
  };

  const handlePreValidation = async () => {
    if (!selectedSession) return;
    setShowPreValidation(true);
    setPreValidationResult(null);
    try {
      const result = await triggerPreValidation(selectedSession.id).unwrap();
      setPreValidationResult(result);
    } catch (err: any) {
      setPreValidationResult(null);
      setShowPreValidation(false);
      alert('Erreur lors de la pré-validation : ' + (err?.data?.error || 'Erreur inconnue'));
    }
  };

  const handleGenerer = async (joursEmpruntes?: { date: string; source: string; cible: string }[]) => {
    if (!selectedSession) return;
    setShowPreValidation(false);
    setPreValidationResult(null);
    try {
      const result = await genererEmploiTemps({
        sessionId: selectedSession.id,
        jours_empruntes: joursEmpruntes,
      }).unwrap();
      setGenerationResult(result);
      refetchSessions();
      if (result.success && result.rapport?.criticite !== 'critique') {
        setActiveTab('visualisation');
      }
    } catch (err: any) {
      setGenerationResult({
        success: false,
        resultats: {},
        total_creneaux: 0,
        jours_utilises: 0,
        erreurs: [err?.data?.error || 'Erreur lors de la génération'],
        avertissements: [],
      });
    }
  };

  const handleExtendPeriod = async (newDateFin: string) => {
    if (!selectedSession) return;
    try {
      const updated = await updateSession({
        id: selectedSession.id,
        data: { date_fin: newDateFin },
      }).unwrap();
      setSelectedSession(updated);
      refetchSessions();
      setShowPreValidation(false);
      setPreValidationResult(null);
      setGenerationResult(null);
      // Re-lancer la pré-validation avec la nouvelle date
      setTimeout(() => handlePreValidation(), 300);
    } catch (err) {
      console.error('Erreur extension période:', err);
    }
  };

  const handleDeleteSession = async (id: number) => {
    if (!window.confirm('Supprimer cette session et tous ses créneaux ?')) return;
    try {
      await deleteSession(id).unwrap();
      if (selectedSession?.id === id) setSelectedSession(null);
      refetchSessions();
    } catch (err) {
      console.error('Erreur suppression:', err);
    }
  };

  const tabs: { key: TabType; label: string; icon: React.ReactNode }[] = [
    { key: 'config', label: 'Configuration', icon: <Settings size={18} /> },
    { key: 'visualisation', label: 'Visualisation', icon: <Eye size={18} /> },
    { key: 'impression', label: 'Impression', icon: <Printer size={18} /> },
    { key: 'recap', label: 'Récap Salles', icon: <Building2 size={18} /> },
    { key: 'sujets', label: 'Sujets à imprimer', icon: <Layers size={18} /> },
    { key: 'etiquettes', label: 'Étiquettes copies', icon: <Tag size={18} /> },
  ];

  return (
    <div className={`p-6 min-h-screen ${isDark ? 'bg-gray-900 text-white' : 'bg-gray-50 text-gray-900'}`}>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <Calendar size={28} className="text-blue-500" />
          <div>
            <h1 className="text-2xl font-bold">Emplois du Temps Examens</h1>
            <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
              Génération et gestion des emplois du temps d'examens — {anneeAcademique}
            </p>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className={`flex gap-1 mb-6 p-1 rounded-lg ${isDark ? 'bg-gray-800' : 'bg-gray-200'}`}>
        {tabs.map((tab) => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            className={`flex items-center gap-2 px-4 py-2.5 rounded-md text-sm font-medium transition ${
              activeTab === tab.key
                ? 'bg-blue-600 text-white shadow'
                : isDark
                  ? 'text-gray-400 hover:text-white hover:bg-gray-700'
                  : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
            }`}
          >
            {tab.icon}
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      {activeTab === 'config' && (
        <div className="space-y-6">
          {/* Sessions existantes */}
          <div className={`rounded-xl border p-6 ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold">Sessions d'examens</h2>
              <button
                onClick={() => setShowCreateForm(!showCreateForm)}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition text-sm"
              >
                <Plus size={16} />
                Nouvelle session
              </button>
            </div>

            {/* Create form */}
            {showCreateForm && (
              <div className={`mb-6 p-4 rounded-lg border ${isDark ? 'bg-gray-750 border-gray-600' : 'bg-gray-50 border-gray-200'}`}>
                <h3 className="font-medium mb-4">Créer une nouvelle session</h3>
                {createError && (
                  <div className="mb-4 p-3 rounded-lg border border-red-300 bg-red-50 dark:bg-red-900/20 dark:border-red-700 text-sm text-red-700 dark:text-red-300">
                    {createError}
                  </div>
                )}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">Nom</label>
                    <input
                      type="text"
                      value={formData.nom}
                      onChange={(e) => setFormData({ ...formData, nom: e.target.value })}
                      className={`w-full px-3 py-2 rounded-lg border text-sm ${isDark ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-300'}`}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Semestre</label>
                    <select
                      value={formData.semestre}
                      onChange={(e) => setFormData({ ...formData, semestre: parseInt(e.target.value) })}
                      className={`w-full px-3 py-2 rounded-lg border text-sm ${isDark ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-300'}`}
                    >
                      <option value={1}>Semestre 1 (impair)</option>
                      <option value={2}>Semestre 2 (pair)</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Type de session</label>
                    <select
                      value={formData.type_session}
                      onChange={(e) => setFormData({ ...formData, type_session: e.target.value })}
                      className={`w-full px-3 py-2 rounded-lg border text-sm ${isDark ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-300'}`}
                    >
                      <option value="ordinaire">Session Ordinaire</option>
                      <option value="rattrapage">Session de Rattrapage</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Date début</label>
                    <input
                      type="date"
                      value={formData.date_debut}
                      onChange={(e) => setFormData({ ...formData, date_debut: e.target.value })}
                      className={`w-full px-3 py-2 rounded-lg border text-sm ${isDark ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-300'}`}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Date fin</label>
                    <input
                      type="date"
                      value={formData.date_fin}
                      onChange={(e) => setFormData({ ...formData, date_fin: e.target.value })}
                      className={`w-full px-3 py-2 rounded-lg border text-sm ${isDark ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-300'}`}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Type formation</label>
                    <select
                      value={formData.type_formation}
                      onChange={(e) => setFormData({ ...formData, type_formation: e.target.value })}
                      className={`w-full px-3 py-2 rounded-lg border text-sm ${isDark ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-300'}`}
                    >
                      <option value="FI">Formation Initiale (FI)</option>
                      <option value="CS">Cours du Soir (CS)</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Mode de conception</label>
                    <select
                      value={formData.mode_conception}
                      onChange={(e) => setFormData({ ...formData, mode_conception: e.target.value })}
                      className={`w-full px-3 py-2 rounded-lg border text-sm ${isDark ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-300'}`}
                    >
                      <option value="automatique">Génération automatique</option>
                      <option value="manuel">Conception manuelle (grille vide)</option>
                    </select>
                  </div>
                </div>
                <div className="flex gap-3 mt-4">
                  <button
                    onClick={handleCreateSession}
                    disabled={creating || !formData.date_debut || !formData.date_fin}
                    className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 text-sm"
                  >
                    {creating ? 'Création...' : 'Créer la session'}
                  </button>
                  <button
                    onClick={() => setShowCreateForm(false)}
                    className={`px-4 py-2 rounded-lg text-sm ${isDark ? 'bg-gray-700 hover:bg-gray-600' : 'bg-gray-200 hover:bg-gray-300'}`}
                  >
                    Annuler
                  </button>
                </div>
              </div>
            )}

            {/* Sessions list */}
            {loadingSessions ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="animate-spin" size={24} />
              </div>
            ) : sessions.length === 0 ? (
              <p className={`text-center py-8 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                Aucune session d'examens. Créez-en une pour commencer.
              </p>
            ) : (
              <div className="space-y-3">
                {sessions.map((session) => (
                  <div
                    key={session.id}
                    onClick={() => setSelectedSession(session)}
                    className={`p-4 rounded-lg border cursor-pointer transition ${
                      selectedSession?.id === session.id
                        ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                        : isDark
                          ? 'border-gray-700 hover:border-gray-600'
                          : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="font-medium">{session.nom}</h3>
                        <div className="flex items-center gap-4 mt-1 text-sm text-gray-500">
                          <span>Semestre {session.semestre}</span>
                          <span>{session.type_session}</span>
                          <span>{session.date_debut} → {session.date_fin}</span>
                          <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                            session.statut === 'genere' ? 'bg-green-100 text-green-800' :
                            session.statut === 'publie' ? 'bg-blue-100 text-blue-800' :
                            session.statut === 'modifie' ? 'bg-yellow-100 text-yellow-800' :
                            'bg-gray-100 text-gray-800'
                          }`}>
                            {session.statut}
                          </span>
                          {session.mode_conception === 'manuel' && (
                            <span className="px-2 py-0.5 rounded text-xs font-medium bg-gray-200 text-gray-800 dark:bg-gray-700 dark:text-gray-200">
                              conception manuelle
                            </span>
                          )}
                          {session.visible_aux_etudiants && (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-blue-600 text-white animate-pulse" title={`Visible depuis ${session.date_visibilite ? new Date(session.date_visibilite).toLocaleString('fr-FR') : ''} par ${session.visibilite_par_nom || ''}`}>
                              👁 Portail
                            </span>
                          )}
                        </div>
                        {session.nombre_creneaux > 0 && (
                          <div className="text-xs text-gray-400 mt-1">
                            {session.nombre_creneaux} créneaux · {session.nombre_jours_utilises} jours
                          </div>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={(e) => handleToggleVisibilite(session, e)}
                          className={`flex items-center gap-1 px-3 py-1.5 rounded-lg text-sm font-medium ${
                            session.visible_aux_etudiants
                              ? 'bg-blue-600 text-white hover:bg-blue-700'
                              : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-200'
                          }`}
                          title={session.visible_aux_etudiants ? 'Masquer aux étudiants' : 'Rendre visible aux étudiants'}
                        >
                          {session.visible_aux_etudiants ? '👁 Visible' : '🚫 Masqué'}
                        </button>
                        {session.mode_conception === 'manuel' ? (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedSession(session);
                              setActiveTab('visualisation');
                            }}
                            className="flex items-center gap-1 px-3 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm"
                            title="Conception manuelle : placer les matières dans la grille"
                          >
                            <Layers size={14} />
                            Concevoir
                          </button>
                        ) : (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handlePreValidation();
                          }}
                          disabled={generating || validating || selectedSession?.id !== session.id}
                          className="flex items-center gap-1 px-3 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 text-sm"
                        >
                          {(generating || validating) && selectedSession?.id === session.id ? (
                            <Loader2 size={14} className="animate-spin" />
                          ) : (
                            <Play size={14} />
                          )}
                          Generer
                        </button>
                        )}
                        <button
                          onClick={async (e) => {
                            e.stopPropagation();
                            if (!session || session.statut === 'brouillon') {
                              alert('Generez d\'abord l\'emploi du temps avant le brassage.');
                              return;
                            }
                            try {
                              const res = await genererBrassage({
                                session_examen_id: session.id,
                              }).unwrap();
                              setBrassageResult(res);
                            } catch (err: any) {
                              alert(err?.data?.error || 'Erreur lors du brassage');
                            }
                          }}
                          disabled={generatingBrassage || selectedSession?.id !== session.id}
                          className="flex items-center gap-1 px-3 py-1.5 bg-amber-600 text-white rounded-lg hover:bg-amber-700 disabled:opacity-50 text-sm"
                        >
                          {generatingBrassage && selectedSession?.id === session.id ? (
                            <Loader2 size={14} className="animate-spin" />
                          ) : (
                            <Shuffle size={14} />
                          )}
                          Brassage
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteSession(session.id);
                          }}
                          className="p-1.5 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Résultat de génération — Rapport complet */}
          {generationResult && generationResult.success && (
            <RapportGeneration
              result={generationResult}
              sessionId={selectedSession?.id}
              onExtendPeriod={handleExtendPeriod}
              onDismiss={() => setGenerationResult(null)}
            />
          )}

          {/* Erreurs de génération (échec) */}
          {generationResult && !generationResult.success && (
            <div className={`rounded-xl border p-6 ${
              isDark ? 'bg-red-900/20 border-red-700' : 'bg-red-50 border-red-200'
            }`}>
              <div className="flex items-center gap-2 mb-3">
                <AlertTriangle size={20} className="text-red-500" />
                <h3 className="font-semibold">Erreurs lors de la generation</h3>
                <button onClick={() => setGenerationResult(null)} className="ml-auto text-gray-400 hover:text-gray-600">
                  <X size={18} />
                </button>
              </div>
              {generationResult.erreurs.length > 0 && (
                <ul className="list-disc list-inside text-sm text-red-500">
                  {generationResult.erreurs.map((e, i) => <li key={i}>{e}</li>)}
                </ul>
              )}
            </div>
          )}

          {/* Résultat de brassage */}
          {brassageResult && (
            <div className={`rounded-xl border p-6 ${isDark ? 'bg-amber-900/20 border-amber-700' : 'bg-amber-50 border-amber-200'}`}>
              <div className="flex items-center gap-2 mb-3">
                <Shuffle size={20} className="text-amber-500" />
                <h3 className="font-semibold">Brassage intelligent</h3>
                <button onClick={() => setBrassageResult(null)} className="ml-auto text-gray-400 hover:text-gray-600">
                  <X size={18} />
                </button>
              </div>

              <p className="text-sm mb-3">{brassageResult.message}</p>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                <div className={`p-3 rounded-lg ${isDark ? 'bg-gray-800' : 'bg-white'}`}>
                  <div className="text-2xl font-bold text-amber-500">{brassageResult.nombre_etudiants}</div>
                  <div className="text-xs text-gray-500">Etudiants places</div>
                </div>
                <div className={`p-3 rounded-lg ${isDark ? 'bg-gray-800' : 'bg-white'}`}>
                  <div className="text-2xl font-bold text-blue-500">{brassageResult.nombre_salles_utilisees}</div>
                  <div className="text-xs text-gray-500">Salles utilisees</div>
                </div>
                <div className={`p-3 rounded-lg ${isDark ? 'bg-gray-800' : 'bg-white'}`}>
                  <div className="text-2xl font-bold text-green-500">{brassageResult.nombre_sites_utilises}</div>
                  <div className="text-xs text-gray-500">Sites utilises</div>
                </div>
                {brassageResult.etudiants_non_places > 0 && (
                  <div className={`p-3 rounded-lg ${isDark ? 'bg-gray-800' : 'bg-white'}`}>
                    <div className="text-2xl font-bold text-red-500">{brassageResult.etudiants_non_places}</div>
                    <div className="text-xs text-gray-500">Non places</div>
                  </div>
                )}
              </div>

              {/* Avertissements */}
              {brassageResult.avertissements && brassageResult.avertissements.length > 0 && (
                <div className="mb-3">
                  <ul className="list-disc list-inside text-sm text-yellow-600">
                    {brassageResult.avertissements.map((a: string, i: number) => <li key={i}>{a}</li>)}
                  </ul>
                </div>
              )}

              {/* Détails par groupe */}
              {brassageResult.resultats_par_groupe && brassageResult.resultats_par_groupe.length > 0 && (
                <div className="space-y-2">
                  <h4 className="text-sm font-medium">Detail par niveau/pole :</h4>
                  {brassageResult.resultats_par_groupe.map((g, i) => (
                    <div key={i} className={`flex items-center justify-between p-2 rounded text-sm ${isDark ? 'bg-gray-800' : 'bg-white'}`}>
                      <div className="flex items-center gap-2">
                        <span className="font-medium">{g.niveau} {g.pole}</span>
                        {g.vague && (
                          <span className={`text-xs px-1.5 py-0.5 rounded ${isDark ? 'bg-indigo-900/40 text-indigo-300' : 'bg-indigo-100 text-indigo-700'}`}>
                            Vague {g.vague}
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-3 text-xs text-gray-500">
                        <span>{g.etudiants} etudiants</span>
                        <span className="text-green-600">{g.places} places</span>
                        {g.capacite && <span>/ {g.capacite} cap.</span>}
                        {g.non_places > 0 && <span className="text-red-500">{g.non_places} non places</span>}
                        {g.erreur && <span className="text-red-500">{g.erreur}</span>}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {activeTab === 'visualisation' && selectedSession && (
        <div className="space-y-4">
          {/* Alertes enseignants multiples */}
          <AlertesEnseignantsPanel sessionId={selectedSession.id} isDark={isDark} />

          {/* Sélecteur niveau/pôle + actions */}
          <div className={`flex flex-wrap gap-4 items-start p-4 rounded-xl border ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
            {/* Niveau : multi-sélection avec bouton "Tous" */}
            <div>
              <label className="block text-xs font-medium mb-1">Niveau</label>
              <div className="flex items-center gap-1">
                {(() => {
                  const allSelected = visuNiveaux.length === 0;
                  return (
                    <button
                      onClick={() => setVisuNiveaux([])}
                      className={`px-2.5 py-1.5 rounded text-xs font-medium border ${
                        allSelected
                          ? 'bg-blue-600 text-white border-blue-600'
                          : isDark ? 'bg-gray-700 border-gray-600 text-gray-300 hover:bg-gray-600' : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                      }`}
                      title="Afficher tous les niveaux"
                    >
                      Tous
                    </button>
                  );
                })()}
                {(['L1', 'L2', 'L3'] as const).map((niv) => {
                  const active = visuNiveaux.includes(niv);
                  return (
                    <button
                      key={niv}
                      onClick={() => {
                        setVisuNiveaux(active
                          ? visuNiveaux.filter((n) => n !== niv)
                          : [...visuNiveaux, niv]);
                      }}
                      className={`px-2.5 py-1.5 rounded text-xs font-medium border ${
                        active
                          ? 'bg-blue-600 text-white border-blue-600'
                          : isDark ? 'bg-gray-700 border-gray-600 text-gray-300 hover:bg-gray-600' : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                      }`}
                    >
                      {niv}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Pôle : multi-sélection avec bouton "Tous" */}
            <div>
              <label className="block text-xs font-medium mb-1">Pôle</label>
              <div className="flex items-center gap-1">
                {(() => {
                  const allSelected = visuPoles.length === 0;
                  return (
                    <button
                      onClick={() => setVisuPoles([])}
                      className={`px-2.5 py-1.5 rounded text-xs font-medium border ${
                        allSelected
                          ? 'bg-blue-600 text-white border-blue-600'
                          : isDark ? 'bg-gray-700 border-gray-600 text-gray-300 hover:bg-gray-600' : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                      }`}
                      title="Afficher tous les pôles"
                    >
                      Tous
                    </button>
                  );
                })()}
                {([
                  { key: 'polytechnique', label: 'Poly' },
                  { key: 'commerce_juridique', label: 'Com/Jur' },
                ] as const).map((p) => {
                  const active = visuPoles.includes(p.key);
                  return (
                    <button
                      key={p.key}
                      onClick={() => {
                        setVisuPoles(active
                          ? visuPoles.filter((x) => x !== p.key)
                          : [...visuPoles, p.key]);
                      }}
                      className={`px-2.5 py-1.5 rounded text-xs font-medium border ${
                        active
                          ? 'bg-blue-600 text-white border-blue-600'
                          : isDark ? 'bg-gray-700 border-gray-600 text-gray-300 hover:bg-gray-600' : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                      }`}
                    >
                      {p.label}
                    </button>
                  );
                })}
              </div>
            </div>

            {niveauxDispo.length > 0 && (
              <div className="text-xs text-gray-500 ml-2 mt-5">
                Disponibles : {niveauxDispo.map(np => `${np.niveau} ${np.pole}`).join(', ')}
              </div>
            )}

            <div className="flex-1" />

            <button
              onClick={() => { setShowVerification(!showVerification); if (!showVerification) refetchVerification(); }}
              className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium transition ${
                showVerification
                  ? 'bg-green-600 text-white'
                  : isDark ? 'bg-gray-700 hover:bg-gray-600 text-green-400' : 'bg-green-50 hover:bg-green-100 text-green-700'
              }`}
            >
              <ShieldCheck size={16} />
              Verification
            </button>

            <button
              onClick={() => setShowDureeConfig(!showDureeConfig)}
              className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium transition ${
                showDureeConfig
                  ? 'bg-purple-600 text-white'
                  : isDark ? 'bg-gray-700 hover:bg-gray-600 text-purple-400' : 'bg-purple-50 hover:bg-purple-100 text-purple-700'
              }`}
            >
              <Clock size={16} />
              Durees
            </button>

            <button
              onClick={() => setShowPlanifConfig(!showPlanifConfig)}
              className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium transition ${
                showPlanifConfig
                  ? 'bg-indigo-600 text-white'
                  : isDark ? 'bg-gray-700 hover:bg-gray-600 text-indigo-400' : 'bg-indigo-50 hover:bg-indigo-100 text-indigo-700'
              }`}
            >
              <Layers size={16} />
              Planification
            </button>

            {/* Bouton : ajouter un jour (crée des créneaux vides sur une nouvelle date) */}
            <button
              onClick={async () => {
                if (!selectedSession) return;
                const dateStr = window.prompt(
                  "Ajouter un jour à l'EDT (format YYYY-MM-DD) :\n\n" +
                  "Si la date est hors de la plage actuelle, les bornes seront étendues.\n" +
                  "Si samedi/dimanche, la composition est activée.\n" +
                  "Si jour férié, il est retiré de la liste.",
                  '',
                );
                if (!dateStr || !/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
                  if (dateStr) alert("Format invalide. Attendu : YYYY-MM-DD (ex: 2026-08-25).");
                  return;
                }
                try {
                  const res = await ajouterJourSession({ sessionId: selectedSession.id, date: dateStr }).unwrap();
                  refetchSessions();
                  alert(
                    `Jour ${res.date} inséré.\nBornes : ${res.date_debut} → ${res.date_fin}\n\nChangements :\n- ${res.changements.join('\n- ')}`
                  );
                } catch (err: any) {
                  alert(err?.data?.error || "Erreur lors de l'ajout du jour");
                }
              }}
              disabled={ajoutingJour}
              className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium transition ${
                isDark ? 'bg-gray-700 hover:bg-gray-600 text-cyan-400' : 'bg-cyan-50 hover:bg-cyan-100 text-cyan-700'
              } disabled:opacity-50`}
              title="Insérer un jour supplémentaire dans l'EDT (crée des créneaux vides)"
            >
              <Calendar size={16} />
              {ajoutingJour ? 'Ajout…' : 'Ajouter un jour'}
            </button>

            {/* Bouton : ajouter un horaire (nouveau créneau appliqué à tous les jours) */}
            <button
              onClick={async () => {
                if (!selectedSession) return;
                const hd = window.prompt(
                  "Nouvel horaire — heure de DÉBUT (format HH:MM) :\n\n" +
                  "Le créneau apparaîtra sur tous les jours de l'EDT (vide s'il\n" +
                  "n'est pas assigné). Vous pourrez ensuite y déposer une matière.",
                  '11:00',
                );
                if (!hd) return;
                if (!/^\d{1,2}:\d{2}$/.test(hd)) { alert("Format invalide (attendu HH:MM)"); return; }
                const hf = window.prompt("Nouvel horaire — heure de FIN (format HH:MM) :", '13:00');
                if (!hf) return;
                if (!/^\d{1,2}:\d{2}$/.test(hf)) { alert("Format invalide (attendu HH:MM)"); return; }
                try {
                  const res = await ajouterHoraireSession({
                    sessionId: selectedSession.id,
                    heure_debut: hd,
                    heure_fin: hf,
                  }).unwrap();
                  alert(res.message + `\n\nCréneaux totaux : ${res.creneaux_horaires.length}`);
                } catch (err: any) {
                  alert(err?.data?.error || "Erreur lors de l'ajout de l'horaire");
                }
              }}
              disabled={ajoutingHoraire}
              className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium transition ${
                isDark ? 'bg-gray-700 hover:bg-gray-600 text-sky-400' : 'bg-sky-50 hover:bg-sky-100 text-sky-700'
              } disabled:opacity-50`}
              title="Ajouter un nouveau créneau horaire (visible sur tous les jours)"
            >
              <Clock size={16} />
              {ajoutingHoraire ? 'Ajout…' : 'Ajouter un horaire'}
            </button>

            {/* Bouton : synchroniser les matières identiques inter-pôles */}
            <button
              onClick={async () => {
                if (!selectedSession) return;
                if (!window.confirm(
                  "Aligner toutes les matières identiques (même nom) présentes dans plusieurs pôles sur un seul (date, heure) ?\n\n" +
                  "Ex : Rédaction Scientifique II Poly/Com/Jur → même slot.\n" +
                  "Slot canonique = celui avec le plus de créneaux (verrou prioritaire).",
                )) return;
                try {
                  const res = await synchroniserCommunes(selectedSession.id).unwrap();
                  const nbConflits = res.conflits?.length || 0;
                  alert(
                    `${res.matieres_alignees} matière(s) alignée(s), ${res.creneaux_deplaces} créneau(x) déplacé(s).` +
                    (nbConflits > 0 ? `\n\n⚠ ${nbConflits} créneau(x) en conflit — à ajuster manuellement.` : '')
                  );
                } catch (err: any) {
                  alert(err?.data?.error || "Erreur lors de la synchronisation");
                }
              }}
              disabled={syncingCommunes}
              className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium transition ${
                isDark ? 'bg-gray-700 hover:bg-gray-600 text-teal-400' : 'bg-teal-50 hover:bg-teal-100 text-teal-700'
              } disabled:opacity-50`}
              title="Aligner les matières identiques présentes dans plusieurs pôles"
            >
              <Layers size={16} />
              {syncingCommunes ? 'Synchro…' : 'Sync matières communes'}
            </button>

            {/* Bouton : diagnostic matières désynchronisées */}
            <button
              onClick={() => selectedSession && setShowDiagnosticMatieres(true)}
              disabled={!selectedSession}
              className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium transition ${
                isDark ? 'bg-gray-700 hover:bg-gray-600 text-amber-400' : 'bg-amber-50 hover:bg-amber-100 text-amber-700'
              } disabled:opacity-50`}
              title="Diagnostic complet des matières non alignées entre options / pôles"
            >
              <AlertTriangle size={16} />
              Diagnostic matières
            </button>
          </div>

          {/* Panneau de vérification de complétude */}
          {showVerification && verification && (
            <div className={`rounded-xl border p-4 ${
              verification.complet
                ? isDark ? 'bg-green-900/20 border-green-700' : 'bg-green-50 border-green-200'
                : isDark ? 'bg-yellow-900/20 border-yellow-700' : 'bg-yellow-50 border-yellow-200'
            }`}>
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  {verification.complet ? (
                    <CheckCircle size={20} className="text-green-500" />
                  ) : (
                    <AlertTriangle size={20} className="text-yellow-500" />
                  )}
                  <h3 className="font-semibold text-sm">
                    {verification.complet
                      ? `Toutes les matieres sont presentes (${verification.options_completes}/${verification.total_options} options completes)`
                      : `${verification.options_incompletes} option(s) incomplete(s) sur ${verification.total_options}`
                    }
                  </h3>
                </div>
                <button onClick={() => setShowVerification(false)} className="text-gray-400 hover:text-gray-600">
                  <X size={18} />
                </button>
              </div>

              {verification.details.filter(d => !d.complet).length > 0 && (
                <div className="space-y-2 max-h-60 overflow-y-auto">
                  {verification.details.filter(d => !d.complet).map((detail, i) => (
                    <div key={i} className={`p-3 rounded-lg text-sm ${isDark ? 'bg-gray-800' : 'bg-white'}`}>
                      <div className="flex items-center justify-between mb-1">
                        <span className="font-medium">
                          {detail.niveau} — {detail.groupe} ({detail.option})
                        </span>
                        <span className={`text-xs px-2 py-0.5 rounded ${isDark ? 'bg-yellow-900/40 text-yellow-400' : 'bg-yellow-100 text-yellow-700'}`}>
                          {detail.matieres_planifiees}/{detail.matieres_attendues} matieres
                        </span>
                      </div>
                      {detail.manquantes.length > 0 && (
                        <div className="text-xs text-red-500 mt-1">
                          Manquantes : {detail.manquantes.join(', ')}
                        </div>
                      )}
                      {detail.en_trop.length > 0 && (
                        <div className="text-xs text-blue-500 mt-1">
                          En trop : {detail.en_trop.join(', ')}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {verification.details.filter(d => d.complet).length > 0 && (
                <div className="mt-2 text-xs text-green-600">
                  Options completes : {verification.details.filter(d => d.complet).map(d => `${d.niveau} ${d.groupe}`).join(', ')}
                </div>
              )}
            </div>
          )}

          {/* Panneau configuration durees personnalisees */}
          {showDureeConfig && selectedSession && (
            <div className={`rounded-xl border p-4 ${isDark ? 'bg-purple-900/20 border-purple-700' : 'bg-purple-50 border-purple-200'}`}>
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <Clock size={18} className="text-purple-500" />
                  <h3 className="font-semibold text-sm">Durees personnalisees par matiere</h3>
                </div>
                <button onClick={() => setShowDureeConfig(false)} className="text-gray-400 hover:text-gray-600">
                  <X size={18} />
                </button>
              </div>

              {/* Durées existantes */}
              {selectedSession.contraintes?.durees_personnalisees &&
                Object.keys(selectedSession.contraintes.durees_personnalisees).length > 0 && (
                <div className="flex flex-wrap gap-2 mb-3">
                  {Object.entries(selectedSession.contraintes.durees_personnalisees).map(([mat, dur]) => (
                    <span key={mat} className={`flex items-center gap-1 px-2 py-1 rounded text-xs font-medium ${
                      isDark ? 'bg-purple-800/40 text-purple-300' : 'bg-purple-100 text-purple-700'
                    }`}>
                      {mat}: {dur}
                      <button
                        onClick={async () => {
                          const newDurees = { ...selectedSession.contraintes!.durees_personnalisees };
                          delete newDurees[mat];
                          await updateSession({
                            id: selectedSession.id,
                            data: { contraintes: { durees_personnalisees: newDurees } },
                          }).unwrap();
                          refetchSessions();
                        }}
                        className="ml-1 hover:text-red-400"
                      >
                        <X size={12} />
                      </button>
                    </span>
                  ))}
                </div>
              )}

              {/* Ajouter une durée */}
              <div className="flex items-end gap-3">
                <div>
                  <label className="block text-xs mb-1">Nom de la matiere</label>
                  <input
                    type="text"
                    value={dureeMatiere}
                    onChange={(e) => setDureeMatiere(e.target.value)}
                    placeholder="Ex: Dessin"
                    className={`px-3 py-2 rounded-lg border text-sm ${isDark ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-300'}`}
                  />
                </div>
                <div>
                  <label className="block text-xs mb-1">Duree (HH:MM)</label>
                  <input
                    type="text"
                    value={dureeValeur}
                    onChange={(e) => setDureeValeur(e.target.value)}
                    placeholder="02:30"
                    className={`w-24 px-3 py-2 rounded-lg border text-sm ${isDark ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-300'}`}
                  />
                </div>
                <button
                  onClick={async () => {
                    if (!dureeMatiere.trim() || !dureeValeur.trim()) return;
                    const existing = selectedSession.contraintes?.durees_personnalisees || {};
                    const newDurees = { ...existing, [dureeMatiere.trim()]: dureeValeur.trim() };
                    try {
                      await updateSession({
                        id: selectedSession.id,
                        data: { contraintes: { durees_personnalisees: newDurees } },
                      }).unwrap();
                      setDureeMatiere('');
                      refetchSessions();
                    } catch (err) {
                      console.error(err);
                    }
                  }}
                  disabled={!dureeMatiere.trim()}
                  className="px-3 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 text-sm"
                >
                  Ajouter
                </button>
              </div>
              <p className="text-xs text-gray-500 mt-2">
                Les matieres avec duree personnalisee auront un horaire different du standard (2H).
                Regenerez l'emploi du temps apres modification.
              </p>
            </div>
          )}

          {/* Panneau planification interleaving */}
          {showPlanifConfig && selectedSession && (
            <div className={`rounded-xl border p-4 ${isDark ? 'bg-indigo-900/20 border-indigo-700' : 'bg-indigo-50 border-indigo-200'}`}>
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <Layers size={18} className="text-indigo-500" />
                  <h3 className="font-semibold text-sm">Planification intelligente (Interleaving)</h3>
                </div>
                <button onClick={() => setShowPlanifConfig(false)} className="text-gray-400 hover:text-gray-600">
                  <X size={18} />
                </button>
              </div>

              {/* Pause entre matières + application immédiate */}
              <PauseControl session={selectedSession} isDark={isDark} onDone={refetchSessions} />

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Alternance L1 / L2+L3 */}
                <label className={`flex items-start gap-3 p-3 rounded-lg cursor-pointer ${isDark ? 'bg-gray-800 hover:bg-gray-750' : 'bg-white hover:bg-gray-50'}`}>
                  <input
                    type="checkbox"
                    checked={selectedSession.contraintes?.alternance_poles ?? true}
                    onChange={async (e) => {
                      try {
                        await updateSession({
                          id: selectedSession.id,
                          data: { contraintes: { alternance_poles: e.target.checked } },
                        }).unwrap();
                        refetchSessions();
                      } catch (err) { console.error(err); }
                    }}
                    className="mt-1"
                  />
                  <div>
                    <div className="text-sm font-medium">Alternance L1 / L2+L3</div>
                    <div className="text-xs text-gray-500 mt-0.5">
                      L1 et L2/L3 composent des jours differents. Les memes salles sont reutilisees
                      (L2/L3 un jour, L1 le lendemain). Tous les poles composent les memes jours.
                    </div>
                  </div>
                </label>

                {/* Alternance L2/L3 */}
                <label className={`flex items-start gap-3 p-3 rounded-lg cursor-pointer ${isDark ? 'bg-gray-800 hover:bg-gray-750' : 'bg-white hover:bg-gray-50'}`}>
                  <input
                    type="checkbox"
                    checked={selectedSession.contraintes?.alternance_l2_l3 ?? false}
                    onChange={async (e) => {
                      try {
                        await updateSession({
                          id: selectedSession.id,
                          data: { contraintes: { alternance_l2_l3: e.target.checked } },
                        }).unwrap();
                        refetchSessions();
                      } catch (err) { console.error(err); }
                    }}
                    className="mt-1"
                  />
                  <div>
                    <div className="text-sm font-medium">Alternance L2/L3 (1j/2)</div>
                    <div className="text-xs text-gray-500 mt-0.5">
                      Si les places sont insuffisantes pour L2+L3 simultanément,
                      L2 et L3 alternent entre eux au sein de leurs jours.
                    </div>
                  </div>
                </label>

                {/* Jour 1 : nb matières configurable */}
                <div className={`flex items-start gap-3 p-3 rounded-lg ${isDark ? 'bg-gray-800' : 'bg-white'}`}>
                  <div className="flex-1">
                    <div className="text-sm font-medium mb-2">Jour 1 : nombre de matières</div>
                    <div className="flex items-center gap-2 mb-2">
                      {[1, 2, 3].map((n) => {
                        const current = selectedSession.contraintes?.nb_matieres_jour1
                          ?? (selectedSession.contraintes?.jour1_une_seule_matiere ? 1 : 2);
                        const active = current === n;
                        return (
                          <button
                            key={n}
                            onClick={async () => {
                              try {
                                await updateSession({
                                  id: selectedSession.id,
                                  data: { contraintes: {
                                    nb_matieres_jour1: n,
                                    jour1_une_seule_matiere: n === 1,
                                  }},
                                }).unwrap();
                                refetchSessions();
                              } catch (err) { console.error(err); }
                            }}
                            className={`px-3 py-1.5 text-sm rounded font-medium border ${
                              active
                                ? 'bg-blue-600 text-white border-blue-600'
                                : isDark ? 'bg-gray-700 border-gray-600 text-gray-300' : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                            }`}
                          >
                            {n} matière{n > 1 ? 's' : ''}
                          </button>
                        );
                      })}
                    </div>
                    <div className="text-xs text-gray-500">
                      <strong>1 matière</strong> = vérification frais (à partir de {selectedSession.contraintes?.jour1_heure_debut || '11:00'}).
                      <strong> 2 matières</strong> = 08H + 11H. <strong>3 matières</strong> = journée complète.
                    </div>
                  </div>
                </div>

                {/* Seuil candidats rattrapage (visible uniquement si type_session=rattrapage) */}
                {selectedSession.type_session === 'rattrapage' && (
                  <div className={`flex items-start gap-3 p-3 rounded-lg border-l-4 border-orange-500 ${isDark ? 'bg-orange-900/10' : 'bg-orange-50'}`}>
                    <div className="flex-1">
                      <div className="text-sm font-medium mb-1">
                        🎯 Rattrapage : seuil minimum d'étudiants par matière
                      </div>
                      <div className="text-xs text-gray-500 mb-2">
                        N'inclut une matière dans le planning que si <strong>au moins ce nombre d'étudiants</strong> doit
                        la rattraper. Permet d'éviter de générer des créneaux pour des matières où 1-2 candidats seulement échouent.
                      </div>
                      <div className="flex items-center gap-2 flex-wrap">
                        {[1, 2, 3, 5, 10].map((n) => {
                          const current = selectedSession.contraintes?.min_candidats_rattrapage ?? 1;
                          const active = current === n;
                          return (
                            <button
                              key={n}
                              onClick={async () => {
                                try {
                                  await updateSession({
                                    id: selectedSession.id,
                                    data: { contraintes: { min_candidats_rattrapage: n }},
                                  }).unwrap();
                                  refetchSessions();
                                } catch (err) { console.error(err); }
                              }}
                              className={`px-3 py-1.5 text-sm rounded font-medium border ${
                                active
                                  ? 'bg-orange-600 text-white border-orange-600'
                                  : isDark ? 'bg-gray-700 border-gray-600 text-gray-300' : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                              }`}
                            >
                              ≥ {n}
                            </button>
                          );
                        })}
                        <span className="text-xs text-gray-400 italic ml-1">
                          actuel : {selectedSession.contraintes?.min_candidats_rattrapage ?? 1}
                        </span>
                      </div>
                      <div className="mt-2 text-[11px] text-orange-700 dark:text-orange-300">
                        💡 Si tu vois des « modules sans créneaux » ou un planning saturé : <strong>augmente ce seuil</strong> à 3 ou 5
                        pour réduire le nombre de matières planifiées. Les étudiants en très petit nombre rattraperont avec
                        une autre vague (gestion manuelle).
                      </div>
                    </div>
                  </div>
                )}

                {/* Nombre de matières par jour (jours normaux) */}
                <div className={`flex items-start gap-3 p-3 rounded-lg ${isDark ? 'bg-gray-800' : 'bg-white'}`}>
                  <div className="flex-1">
                    <div className="text-sm font-medium mb-2">Nombre maximum de matières par jour (jours normaux)</div>
                    <div className="flex items-center gap-2 mb-2 flex-wrap">
                      {[2, 3, 4, 5].map((n) => {
                        const current = selectedSession.contraintes?.max_matieres_par_jour ?? 3;
                        const active = current === n;
                        return (
                          <button
                            key={n}
                            onClick={async () => {
                              try {
                                await updateSession({
                                  id: selectedSession.id,
                                  data: { contraintes: { max_matieres_par_jour: n }},
                                }).unwrap();
                                refetchSessions();
                              } catch (err) { console.error(err); }
                            }}
                            className={`px-3 py-1.5 text-sm rounded font-medium border ${
                              active
                                ? 'bg-blue-600 text-white border-blue-600'
                                : isDark ? 'bg-gray-700 border-gray-600 text-gray-300' : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                            }`}
                          >
                            {n} matière{n > 1 ? 's' : ''}
                          </button>
                        );
                      })}
                    </div>
                    <div className="text-xs text-gray-500">
                      <strong>3 matières</strong> = créneaux standards (08H, 11H, 14H). <strong>4-5 matières</strong> = ajoute 17H et 19H.
                    </div>
                  </div>
                </div>

                {/* Max matieres L3 */}
                <div className={`flex items-start gap-3 p-3 rounded-lg ${isDark ? 'bg-gray-800' : 'bg-white'}`}>
                  <div className="flex-1">
                    <div className="text-sm font-medium mb-2">Nombre maximum de matières par jour pour les <strong>L3</strong></div>
                    <div className="flex items-center gap-2 mb-2">
                      {[1, 2, 3].map((n) => {
                        const current = selectedSession.contraintes?.max_matieres_l3 ?? 2;
                        const active = current === n;
                        return (
                          <button
                            key={n}
                            onClick={async () => {
                              try {
                                await updateSession({
                                  id: selectedSession.id,
                                  data: { contraintes: { max_matieres_l3: n }},
                                }).unwrap();
                                refetchSessions();
                              } catch (err) { console.error(err); }
                            }}
                            className={`px-3 py-1.5 text-sm rounded font-medium border ${
                              active
                                ? 'bg-purple-600 text-white border-purple-600'
                                : isDark ? 'bg-gray-700 border-gray-600 text-gray-300' : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                            }`}
                          >
                            {n} matière{n > 1 ? 's' : ''}
                          </button>
                        );
                      })}
                    </div>
                    <div className="text-xs text-gray-500">
                      Les L3 ont souvent moins de matières mais qui durent plus longtemps. Augmente cette valeur pour caser plus de rattrapages L3.
                    </div>
                  </div>
                </div>

                {/* Jours fériés à exclure */}
                <div className={`p-3 rounded-lg ${isDark ? 'bg-gray-800' : 'bg-white'}`}>
                  <div className="text-sm font-medium mb-2">Jours fériés à exclure</div>
                  <div className="text-xs text-gray-500 mb-2">
                    Dates exclues de la planification (ex : Ascension, Fête nationale).
                    La période s'étendra sur les jours utiles restants.
                  </div>
                  <div className="flex items-center gap-2">
                    <input
                      type="date"
                      min={selectedSession.date_debut}
                      max={selectedSession.date_fin}
                      onChange={async (e) => {
                        const newDate = e.target.value;
                        if (!newDate) return;
                        const current = selectedSession.contraintes?.jours_feries || [];
                        if (current.includes(newDate)) return;
                        try {
                          await updateSession({
                            id: selectedSession.id,
                            data: { contraintes: { jours_feries: [...current, newDate].sort() } },
                          }).unwrap();
                          refetchSessions();
                          e.target.value = '';
                        } catch (err) { console.error(err); }
                      }}
                      className={`px-2 py-1 text-sm rounded border ${
                        isDark ? 'bg-gray-700 border-gray-600 text-gray-200' : 'bg-white border-gray-300 text-gray-700'
                      }`}
                    />
                    <span className="text-xs text-gray-500">+ pour ajouter une date</span>
                  </div>
                  {(selectedSession.contraintes?.jours_feries?.length ?? 0) > 0 && (
                    <div className="flex flex-wrap gap-1.5 mt-2">
                      {(selectedSession.contraintes?.jours_feries || []).map((d) => (
                        <span
                          key={d}
                          className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs ${
                            isDark ? 'bg-orange-900/40 text-orange-300 border border-orange-800' : 'bg-orange-50 text-orange-700 border border-orange-200'
                          }`}
                        >
                          {new Date(d).toLocaleDateString('fr-FR', { weekday: 'short', day: 'numeric', month: 'short' })}
                          <button
                            onClick={async () => {
                              const current = selectedSession.contraintes?.jours_feries || [];
                              try {
                                await updateSession({
                                  id: selectedSession.id,
                                  data: { contraintes: { jours_feries: current.filter((x) => x !== d) } },
                                }).unwrap();
                                refetchSessions();
                              } catch (err) { console.error(err); }
                            }}
                            className="ml-0.5 hover:text-red-500"
                            title="Retirer ce jour férié"
                          >×</button>
                        </span>
                      ))}
                    </div>
                  )}
                </div>

                {/* Composer samedi + nombre matieres samedi */}
                <div className={`flex items-start gap-3 p-3 rounded-lg ${isDark ? 'bg-gray-800' : 'bg-white'}`}>
                  <input
                    type="checkbox"
                    checked={selectedSession.contraintes?.composer_samedi ?? true}
                    onChange={async (e) => {
                      try {
                        await updateSession({
                          id: selectedSession.id,
                          data: { contraintes: { composer_samedi: e.target.checked } },
                        }).unwrap();
                        refetchSessions();
                      } catch (err) { console.error(err); }
                    }}
                    className="mt-1"
                  />
                  <div className="flex-1">
                    <div className="text-sm font-medium mb-2">Examens le samedi</div>
                    {selectedSession.contraintes?.composer_samedi && (
                      <div>
                        <div className="text-xs text-gray-500 mb-2">
                          Nombre maximum de matières le samedi :
                        </div>
                        <div className="flex items-center gap-2">
                          {[1, 2, 3].map((n) => {
                            const current = selectedSession.contraintes?.max_matieres_samedi ?? 2;
                            const active = current === n;
                            return (
                              <button
                                key={n}
                                onClick={async () => {
                                  try {
                                    await updateSession({
                                      id: selectedSession.id,
                                      data: { contraintes: { max_matieres_samedi: n }},
                                    }).unwrap();
                                    refetchSessions();
                                  } catch (err) { console.error(err); }
                                }}
                                className={`px-3 py-1.5 text-sm rounded font-medium border ${
                                  active
                                    ? 'bg-blue-600 text-white border-blue-600'
                                    : isDark ? 'bg-gray-700 border-gray-600 text-gray-300' : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                                }`}
                              >
                                {n} matière{n > 1 ? 's' : ''}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Filtre : niveaux à générer */}
                <div className={`flex items-start gap-3 p-3 rounded-lg ${isDark ? 'bg-gray-800' : 'bg-white'}`}>
                  <div className="flex-1">
                    <div className="text-sm font-medium mb-2">Niveaux à générer</div>
                    <div className="text-xs text-gray-500 mb-2">
                      Vide = tous. Les niveaux non cochés conservent leurs créneaux actuels.
                    </div>
                    <div className="flex items-center gap-2">
                      {(['L1', 'L2', 'L3'] as const).map((niv) => {
                        const current = (selectedSession.contraintes?.niveaux_filtre || []) as string[];
                        const active = current.includes(niv);
                        return (
                          <button
                            key={niv}
                            onClick={async () => {
                              const next = active
                                ? current.filter((n) => n !== niv)
                                : [...current, niv];
                              try {
                                await updateSession({
                                  id: selectedSession.id,
                                  data: { contraintes: { niveaux_filtre: next } },
                                }).unwrap();
                                refetchSessions();
                              } catch (err) { console.error(err); }
                            }}
                            className={`px-3 py-1.5 text-sm rounded font-medium border ${
                              active
                                ? 'bg-blue-600 text-white border-blue-600'
                                : isDark ? 'bg-gray-700 border-gray-600 text-gray-300' : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                            }`}
                          >
                            {niv}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </div>

                {/* Filtre : pôles à générer */}
                <div className={`flex items-start gap-3 p-3 rounded-lg ${isDark ? 'bg-gray-800' : 'bg-white'}`}>
                  <div className="flex-1">
                    <div className="text-sm font-medium mb-2">Pôles à générer</div>
                    <div className="text-xs text-gray-500 mb-2">
                      Vide = tous. Utile pour générer indépendamment Polytechnique et Commerce/Juridique.
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                      {(['polytechnique', 'commerce_juridique'] as const).map((pole) => {
                        const current = (selectedSession.contraintes?.poles_filtre || []) as string[];
                        const active = current.includes(pole);
                        const label = pole === 'polytechnique' ? 'Polytechnique' : 'Commerce/Juridique';
                        return (
                          <button
                            key={pole}
                            onClick={async () => {
                              const next = active
                                ? current.filter((p) => p !== pole)
                                : [...current, pole];
                              try {
                                await updateSession({
                                  id: selectedSession.id,
                                  data: { contraintes: { poles_filtre: next } },
                                }).unwrap();
                                refetchSessions();
                              } catch (err) { console.error(err); }
                            }}
                            className={`px-3 py-1.5 text-sm rounded font-medium border ${
                              active
                                ? 'bg-blue-600 text-white border-blue-600'
                                : isDark ? 'bg-gray-700 border-gray-600 text-gray-300' : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                            }`}
                          >
                            {label}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </div>

                {/* Premier jour : L1 ou L2/L3 */}
                {(selectedSession.contraintes?.alternance_poles ?? true) && (
                  <div className={`flex items-start gap-3 p-3 rounded-lg ${isDark ? 'bg-gray-800' : 'bg-white'}`}>
                    <div className="flex-1">
                      <div className="text-sm font-medium mb-2">Qui commence le jour 1</div>
                      <div className="text-xs text-gray-500 mb-2">
                        En cas d'alternance, ce groupe compose le premier jour et les jours d'index pair.
                      </div>
                      <div className="flex items-center gap-2">
                        {(['L2L3', 'L1'] as const).map((grp) => {
                          const current = (selectedSession.contraintes?.groupe_premier_jour || 'L2L3') as string;
                          const active = current === grp;
                          const label = grp === 'L2L3' ? 'L2 et L3' : 'L1';
                          return (
                            <button
                              key={grp}
                              onClick={async () => {
                                try {
                                  await updateSession({
                                    id: selectedSession.id,
                                    data: { contraintes: { groupe_premier_jour: grp } },
                                  }).unwrap();
                                  refetchSessions();
                                } catch (err) { console.error(err); }
                              }}
                              className={`px-3 py-1.5 text-sm rounded font-medium border ${
                                active
                                  ? 'bg-blue-600 text-white border-blue-600'
                                  : isDark ? 'bg-gray-700 border-gray-600 text-gray-300' : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                              }`}
                            >
                              {label}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                )}

                {/* Répartition manuelle des jours */}
                <div className={`flex items-start gap-3 p-3 rounded-lg md:col-span-2 ${isDark ? 'bg-gray-800' : 'bg-white'}`}>
                  <div className="flex-1">
                    <div className="flex items-center justify-between mb-2">
                      <div className="text-sm font-medium">Répartition manuelle des jours</div>
                      {selectedSession.contraintes?.repartition_jours_manuelle &&
                       Object.keys(selectedSession.contraintes.repartition_jours_manuelle).length > 0 && (
                        <button
                          onClick={async () => {
                            try {
                              await updateSession({
                                id: selectedSession.id,
                                data: { contraintes: { repartition_jours_manuelle: {} } },
                              }).unwrap();
                              refetchSessions();
                            } catch (err) { console.error(err); }
                          }}
                          className="text-xs text-red-600 hover:underline"
                        >
                          Réinitialiser
                        </button>
                      )}
                    </div>
                    <div className="text-xs text-gray-500 mb-3">
                      Force la répartition L1 / L2-L3 par jour. Les jours non spécifiés utilisent
                      l'alternance automatique. Ajouter un jour par date.
                    </div>
                    {(() => {
                      const rep = (selectedSession.contraintes?.repartition_jours_manuelle || {}) as Record<string, string[]>;
                      const dates = Object.keys(rep).sort();
                      const saveRep = async (next: Record<string, string[]>) => {
                        try {
                          await updateSession({
                            id: selectedSession.id,
                            data: { contraintes: { repartition_jours_manuelle: next } },
                          }).unwrap();
                          refetchSessions();
                        } catch (err) { console.error(err); }
                      };
                      return (
                        <div className="space-y-2">
                          {dates.map((d) => {
                            const grp = rep[d] || [];
                            const isL1 = grp.includes('L1');
                            const isL2L3 = grp.includes('L2') || grp.includes('L3');
                            return (
                              <div key={d} className="flex items-center gap-2">
                                <input
                                  type="date"
                                  value={d}
                                  min={selectedSession.date_debut}
                                  max={selectedSession.date_fin}
                                  onChange={(e) => {
                                    const nd = e.target.value;
                                    if (!nd || nd === d) return;
                                    const next = { ...rep };
                                    delete next[d];
                                    next[nd] = grp;
                                    saveRep(next);
                                  }}
                                  className={`px-2 py-1 text-xs rounded border ${isDark ? 'bg-gray-700 border-gray-600 text-gray-200' : 'bg-white border-gray-300'}`}
                                />
                                <button
                                  onClick={() => {
                                    const next = { ...rep };
                                    const g = new Set(grp);
                                    if (isL1) g.delete('L1'); else g.add('L1');
                                    next[d] = Array.from(g);
                                    if (next[d].length === 0) delete next[d];
                                    saveRep(next);
                                  }}
                                  className={`px-2 py-1 text-xs rounded border ${
                                    isL1
                                      ? 'bg-blue-600 text-white border-blue-600'
                                      : isDark ? 'bg-gray-700 border-gray-600 text-gray-300' : 'bg-white border-gray-300 text-gray-700'
                                  }`}
                                >
                                  L1
                                </button>
                                <button
                                  onClick={() => {
                                    const next = { ...rep };
                                    const g = new Set(grp);
                                    if (isL2L3) { g.delete('L2'); g.delete('L3'); }
                                    else { g.add('L2'); g.add('L3'); }
                                    next[d] = Array.from(g);
                                    if (next[d].length === 0) delete next[d];
                                    saveRep(next);
                                  }}
                                  className={`px-2 py-1 text-xs rounded border ${
                                    isL2L3
                                      ? 'bg-blue-600 text-white border-blue-600'
                                      : isDark ? 'bg-gray-700 border-gray-600 text-gray-300' : 'bg-white border-gray-300 text-gray-700'
                                  }`}
                                >
                                  L2/L3
                                </button>
                                <button
                                  onClick={() => {
                                    const next = { ...rep };
                                    delete next[d];
                                    saveRep(next);
                                  }}
                                  className="ml-auto text-xs text-red-600 hover:underline"
                                >
                                  Supprimer
                                </button>
                              </div>
                            );
                          })}
                          <button
                            onClick={() => {
                              const today = selectedSession.date_debut;
                              const key = today && !rep[today] ? today : selectedSession.date_fin;
                              if (rep[key]) return;
                              const next = { ...rep, [key]: ['L2', 'L3'] };
                              saveRep(next);
                            }}
                            className={`px-3 py-1.5 text-xs rounded border ${isDark ? 'bg-gray-700 border-gray-600 text-gray-300 hover:bg-gray-600' : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'}`}
                          >
                            + Ajouter un jour
                          </button>
                        </div>
                      );
                    })()}
                  </div>
                </div>
              </div>

              <div className={`mt-3 p-3 rounded-lg text-xs ${isDark ? 'bg-gray-800 text-gray-400' : 'bg-white text-gray-500'}`}>
                <strong>Planification :</strong> Chaque niveau utilise les 3 creneaux
                (08H, 11H, 14H). {selectedSession.contraintes?.alternance_poles
                  ? 'L1 et L2/L3 alternent les jours (memes salles reutilisees). Tous les poles composent les memes jours.'
                  : 'Tous les niveaux passent les memes jours.'}
                {selectedSession.contraintes?.alternance_l2_l3
                  ? ' L2 et L3 alternent entre eux (1j/2) au sein de leurs jours.'
                  : ''}
                {' '}Poly → Site 4, Com/Jur → Sites 1,2,3 (configurable). Regenerez apres modification.
              </div>
            </div>
          )}

          {/* Tableau(x) — un par combinaison niveau × pôle sélectionnée
              (conception manuelle : grille vide + matières à placer, 2026-09-14) */}
          {selectedSession.mode_conception === 'manuel' ? (
            <ConceptionManuelle session={selectedSession} isDark={isDark} />
          ) : (() => {
            // Bases : soit filtrées par l'utilisateur, soit toutes celles listées comme disponibles
            const niveauxTous: string[] = ['L1', 'L2', 'L3'];
            const polesTous: string[] = ['polytechnique', 'commerce_juridique'];
            const nivFiltre = visuNiveaux.length ? visuNiveaux : niveauxTous;
            const polFiltre = visuPoles.length ? visuPoles : polesTous;

            // Ne rend que les combinaisons présentes dans niveauxDispo pour éviter les tableaux vides
            const combos = niveauxDispo.length > 0
              ? niveauxDispo.filter(np => nivFiltre.includes(np.niveau) && polFiltre.includes(np.pole))
              : nivFiltre.flatMap(n => polFiltre.map(p => ({ niveau: n, pole: p })));

            // Fallback single-sélection pour rétrocompatibilité si aucun filtre et pas de liste dispo
            if (combos.length === 0) {
              return (
                <EmploiTempsTableau
                  sessionId={selectedSession.id}
                  niveau={selectedNiveau}
                  pole={selectedPole}
                  isDark={isDark}
                />
              );
            }

            // 1 seul tableau : rendu direct (pas de header supplémentaire)
            if (combos.length === 1) {
              return (
                <EmploiTempsTableau
                  sessionId={selectedSession.id}
                  niveau={combos[0].niveau}
                  pole={combos[0].pole}
                  isDark={isDark}
                />
              );
            }

            // Multiples : chaque tableau précédé d'un header identifiant la combinaison
            return (
              <div className="space-y-6">
                {combos.map(({ niveau, pole }) => {
                  const poleLabel = pole === 'polytechnique' ? 'Polytechnique'
                                  : pole === 'commerce_juridique' ? 'Commerce & Juridique'
                                  : pole;
                  return (
                    <div key={`${niveau}-${pole}`}>
                      <div className={`px-3 py-2 rounded-t-lg text-sm font-semibold ${
                        isDark ? 'bg-gray-800 text-gray-200 border border-gray-700 border-b-0' : 'bg-blue-50 text-blue-900 border border-blue-200 border-b-0'
                      }`}>
                        {niveau} — Pôle {poleLabel}
                      </div>
                      <EmploiTempsTableau
                        sessionId={selectedSession.id}
                        niveau={niveau}
                        pole={pole}
                        isDark={isDark}
                      />
                    </div>
                  );
                })}
              </div>
            );
          })()}
        </div>
      )}

      {activeTab === 'visualisation' && !selectedSession && (
        <div className={`text-center py-16 rounded-xl border ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
          <Calendar size={48} className="mx-auto text-gray-400 mb-4" />
          <p className="text-gray-500">Sélectionnez ou créez une session dans l'onglet Configuration</p>
        </div>
      )}

      {activeTab === 'impression' && selectedSession && (
        <EmploiTempsImpression
          sessionId={selectedSession.id}
          isDark={isDark}
        />
      )}

      {activeTab === 'impression' && !selectedSession && (
        <div className={`text-center py-16 rounded-xl border ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
          <Printer size={48} className="mx-auto text-gray-400 mb-4" />
          <p className="text-gray-500">Sélectionnez une session pour imprimer</p>
        </div>
      )}

      {activeTab === 'recap' && selectedSession && (
        <RecapSalles
          sessionId={selectedSession.id}
          isDark={isDark}
        />
      )}

      {activeTab === 'recap' && !selectedSession && (
        <div className={`text-center py-16 rounded-xl border ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
          <Building2 size={48} className="mx-auto text-gray-400 mb-4" />
          <p className="text-gray-500">Sélectionnez une session pour voir le récapitulatif des salles</p>
        </div>
      )}

      {activeTab === 'sujets' && selectedSession && (
        <EstimationSujets
          sessionId={selectedSession.id}
          isDark={isDark}
        />
      )}

      {activeTab === 'sujets' && !selectedSession && (
        <div className={`text-center py-16 rounded-xl border ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
          <Layers size={48} className="mx-auto text-gray-400 mb-4" />
          <p className="text-gray-500">Sélectionnez une session pour estimer les sujets à imprimer</p>
        </div>
      )}

      {activeTab === 'etiquettes' && selectedSession && (
        <EtiquettesImpression session={selectedSession} isDark={isDark} />
      )}

      {activeTab === 'etiquettes' && !selectedSession && (
        <div className={`text-center py-16 rounded-xl border ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
          <Tag size={48} className="mx-auto text-gray-400 mb-4" />
          <p className="text-gray-500">Sélectionnez une session pour imprimer les étiquettes copies</p>
        </div>
      )}
      {/* Modal de pré-validation */}
      {showPreValidation && (
        <PreValidationModal
          validation={preValidationResult}
          isLoading={validating}
          contraintes={
            // Toujours prendre la version live depuis la liste (refetch après updateSession)
            selectedSession
              ? sessions.find(s => s.id === selectedSession.id)?.contraintes ?? selectedSession.contraintes
              : undefined
          }
          dateDebut={selectedSession?.date_debut}
          dateFin={selectedSession?.date_fin}
          onGenerate={handleGenerer}
          onExtendPeriod={handleExtendPeriod}
          onApplyContraintes={async (payload) => {
            if (!selectedSession) return;
            try {
              await updateSession({
                id: selectedSession.id,
                data: { contraintes: payload as any },
              }).unwrap();
              await refetchSessions();
              // Re-lance la pré-validation après modif
              try {
                const res = await triggerPreValidation(selectedSession.id).unwrap();
                setPreValidationResult(res);
              } catch (e) { console.error(e); }
            } catch (err) { console.error(err); alert("Erreur lors de l'application"); }
          }}
          onClose={() => { setShowPreValidation(false); setPreValidationResult(null); }}
        />
      )}

      {/* Modal Diagnostic matières désynchronisées */}
      {showDiagnosticMatieres && selectedSession && (
        <DiagnosticMatieresModal
          sessionId={selectedSession.id}
          isDark={isDark}
          onClose={() => setShowDiagnosticMatieres(false)}
        />
      )}
    </div>
  );
};

export default EmploisTempsPage;
